import { SkeletonLoader } from '@toggle/design-system';
import { useScrollListener } from '@toggle/helpers';
import { subDays } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertsCard } from '~/modules/alerts-card/AlertsCard';
import { usePostAlerts } from '~/react-query-hooks/events/use-event-alerts/usePostAlerts';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';

import { ErrorMessageWrapper } from '../error-message-wrapper/ErrorMessageWrapper';
import styles from './AlertsDropdown.module.scss';
import { Footer } from './components/footer/Footer';
import { Header } from './components/header/Header';
import { EmptyState } from './modules/empty-state/EmptyState';

interface AlertsDropdownProps {
  onClose: () => void;
}

export const AlertsDropdown = ({ onClose }: AlertsDropdownProps) => {
  useTranslation(['alerts', 'whatsNew', 'common', 'widget'], {
    useSuspense: false,
  });
  const { trackAlertsDropdownOpen } = usePosthogEvents();
  const [activeButtonIndex, setActiveButtonIndex] = useState(0);
  const isUnreadTab = activeButtonIndex === 1;
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const dateFrom = useMemo(() => subDays(new Date(), 1).toISOString(), []);

  const {
    isError,
    isLoading,
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePostAlerts({
    limit: 10,
    date_from: dateFrom,
    ...(isUnreadTab && { read: false }),
  });

  const alerts = (data?.pages?.flatMap(page => page.alerts) ?? []).filter(
    (alert): alert is NonNullable<typeof alert> =>
      !!alert && (isUnreadTab ? !alert.read : true)
  );

  useEffect(() => {
    const currentContentRef = contentRef.current;

    if (!currentContentRef) {
      return;
    }

    setHasOverflow(
      currentContentRef.scrollHeight > currentContentRef.clientHeight
    );
  }, [isLoading, activeButtonIndex, alerts.length]);

  useEffect(() => {
    trackAlertsDropdownOpen(POSTHOG_TRACKING_SOURCES.ALERTS_DROPDOWN);
  }, []);

  useScrollListener(
    contentRef,
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage, isFetchingNextPage],
    { isGlobalScrollListened: false, offset: 120 }
  );

  const showEmptyState = alerts.length === 0 && !isLoading;
  const showFooter = !showEmptyState && !isError && !isLoading && !hasNextPage;

  const handleButtonClick = (index: number) => setActiveButtonIndex(index);

  const getContent = () => {
    if (isLoading) {
      return (
        <div className={styles.skeletonLoaderWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '100%', borderRadius: '8px' },
              },
            ]}
          />
        </div>
      );
    }

    if (isError) {
      return <ErrorMessageWrapper showBorder={false} displayIcon />;
    }

    if (showEmptyState) {
      return <EmptyState onCloseDropdown={onClose} />;
    }

    return (
      <>
        {alerts.map(alert => (
          <AlertsCard
            key={alert.id}
            alertEvent={alert}
            onNavigate={onClose}
            trackingSource={POSTHOG_TRACKING_SOURCES.ALERTS_DROPDOWN}
          />
        ))}
        {hasNextPage && isFetchingNextPage && (
          <div className={styles.paginationLoaderWrapper}>
            <SkeletonLoader
              areas={[
                {
                  styles: {
                    width: '100%',
                    height: '60px',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.dropdownContainer}>
      <Header
        activeButtonIndex={activeButtonIndex}
        dateFrom={dateFrom}
        onButtonClick={handleButtonClick}
        onClose={onClose}
        shouldHideTabs={(showEmptyState && !isUnreadTab) || isLoading}
      />
      <div className={styles.contentWrapper} ref={contentRef}>
        {getContent()}
        {showFooter && <Footer hasScroll={hasOverflow} onClose={onClose} />}
      </div>
    </div>
  );
};
